import type {
  CustomField,
  LeadTag,
  LeadWithDeps,
  RichLead,
} from '../../lead.popup/types';
import type { AdditionalContact, LeadListItem } from '../../types';
import type { UnitInterestedLead } from './unitApi';

import { cache } from '../cache';
import { type ApiResponse, estClient } from './estClient';
import { NotFoundError } from './httpClient';
import {
  fixAppeal,
  fixBooking,
  fixDeal,
  fixInterest,
  fixLead,
  fixOpportunity,
  fixUnit,
} from './normalizationFns';

export interface LeadStatus {
  id: string;
  name: string;
  order: string;
  isDefault?: boolean;
}

export interface LeadUpdateProps extends RichLead {
  /**
   * Additional prop when updating lead segmentation
   */
  segmentation_objects?: { [id: string]: string }[];

  /**
   * Additional prop when updating lead segmentation
   */
  segmentation_tags?: { [id: string]: string }[];

  /**
   * In order to update custom_fields we need to send them as a part of lead object
   */
  custom_fields?: {
    [id: string]: string;
  };
}

export interface NewLeadProps {
  name: string;
  objects: { [id: string]: string }[];
  tags: { [id: string]: string }[];
  note: string;
  phone: string;
  email: string;
  status_id: string;
  custom_fields: unknown[];
  contacts: { value: string }[];
  custom_note: string;
  ad_source_id: string | null;
}

interface MergeResult {
  success: boolean;
  message: string;
  lead: RichLead;
}

class LeadApi {
  async getAll({
    fields,
    onlyActive,
    onlyMine,
    needsReply,
    ...query
  }: {
    limit?: number;
    offset?: number;
    query?: string;

    /**
     * Filter only leads with at least one active opportunity
     */
    onlyActive?: boolean;
    /**
     * Filter leads where current user is updated_by or manager of an active opportunity.
     * Combined with onlyActive: only leads where current user is manager of an active opportunity.
     */
    onlyMine?: boolean;
    /**
     * Filter leads with at least one EasyBusy chat needing a reply
     */
    needsReply?: boolean;
    fields?: string[];
    order_by?: string;
    order?: 'desc' | 'asc';
  }): Promise<{ rows: LeadListItem[]; total: string }> {
    return estClient.get<{ rows: LeadListItem[]; total: string }>(
      `/api/lead/all`,
      {
        query: {
          ...query,
          only_active: onlyActive ? 1 : undefined,
          only_mine: onlyMine ? 1 : undefined,
          needs_reply: needsReply ? 1 : undefined,
          'fields[]': fields,
        },
      },
    );
  }

  async get(leadId: string): Promise<ApiResponse<LeadWithDeps> | null> {
    try {
      const resp = await estClient.get<ApiResponse<LeadWithDeps>>(
        `/api/fe/lead/${leadId}`,
      );

      // some of our APIs can not handle new number ids
      // convert back to string for compatibility

      fixLead(resp.lead);

      for (const unit of resp.units) {
        fixUnit(unit);
      }

      for (const interest of resp.interests) {
        fixInterest(interest);
      }

      for (const booking of resp.bookings) {
        fixBooking(booking);
      }

      for (const deal of resp.deals) {
        fixDeal(deal);
      }

      for (const opportunity of resp.opportunities) {
        fixOpportunity(opportunity);
      }

      for (const appeal of resp.appeals) {
        fixAppeal(appeal);
      }

      return resp;
    } catch (error) {
      if (error instanceof NotFoundError) {
        return null;
      }

      throw error;
    }
  }

  async create(lead: NewLeadProps): Promise<RichLead> {
    return estClient.post<RichLead>(`/api/lead/create_complete`, {
      body: lead,
    });
  }

  async update(leadId: string, lead: LeadUpdateProps): Promise<void> {
    return estClient.post<void>(`/api/lead/${leadId}/update`, {
      body: lead,
    });
  }

  async getStatuses(): Promise<LeadStatus[]> {
    return cache.get('lead-statuses', () =>
      estClient.get<LeadStatus[]>('/api/lead/status'),
    );
  }

  async updateStatus({
    leadId,
    statusId,
  }: {
    leadId: string;
    statusId: string;
  }): Promise<void> {
    await estClient.post(`/api/lead/${leadId}/update_status`, {
      body: {
        status_id: statusId,
      },
    });
  }

  async getTags(): Promise<LeadTag[]> {
    return cache.get('lead-tags', () =>
      estClient.get<LeadTag[]>('/api/lead/tags'),
    );
  }

  async getCustomFields(): Promise<CustomField[]> {
    return cache.get('lead-customFields', () =>
      estClient.get<CustomField[]>('/api/lead/custom_fields'),
    );
  }

  async createAdditionalContact(
    leadId: string,
    contact: AdditionalContact,
  ): Promise<AdditionalContact> {
    return estClient.post<AdditionalContact>(
      `/api/lead/${leadId}/create_additional`,
      {
        body: {
          additional_contacts: contact,
        },
      },
    );
  }

  async updateAdditionalContact(
    leadId: string,
    { contact, index }: { contact: AdditionalContact; index: number },
  ): Promise<void> {
    return estClient.post<void>(`/api/lead/${leadId}/update_additional`, {
      body: {
        additional_contacts: contact,
        additional_id: index,
      },
    });
  }

  async merge({
    sourceId,
    destinationId,
    contactType,
  }: {
    sourceId: string;
    destinationId: string;
    contactType: 'main' | 'additional';
  }): Promise<MergeResult> {
    const destinationType = { main: 1, additional: 0 }[contactType];

    return estClient.post<MergeResult>(
      `/api/lead/merge/${sourceId}/${destinationId}/${destinationType}`,
    );
  }

  /**
   * Search by lead name, phone, notes
   */
  async search({ query }: { query: string }) {
    return estClient.get<LeadSearchResult[]>('/api/lead/search', {
      query: {
        query,
      },
    });
  }

  async searchById({ query }: { query: string }) {
    return estClient.get<LeadSearchResult[]>('/api/fe/lead/search_by_id', {
      query: {
        query,
      },
    });
  }

  async searchByPhone({ phone }: { phone: string }) {
    return estClient.post<LeadSearchResult[]>('/api/lead/search_by_phone', {
      body: { phone },
    });
  }
}

export type LeadSearchResult = {
  id: string;
  name: string;
  phone: string;
  email: string;
  note: string;
};

export type LeadInterestProps = UnitInterestedLead & {
  unit_id: string;
  lead_id: string;
};

export const leadApi = new LeadApi();
